import type { UseDraggableArguments } from '@dnd-kit/core'
import { useDraggable } from '@dnd-kit/core'
import clsx from 'clsx'
import { rem } from 'polished'
import { useContext, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import DraggedItemHeightContext from './DraggedItemHeightContext'

import IconButton from 'LuxKit/Button/IconButton'
import LineDraggabledotsIcon from 'LuxKit/Icons/line/LineDraggabledotsIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import TripContext from 'tripPlanner/contexts/TripContext'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'
import Tooltip from 'LuxKit/Tooltip'

const Root = styled.div`
  position: relative;
  max-height: auto;

  &.collapsed {
    max-height: 0;
    overflow: hidden;
    padding-bottom: 0;
  }
`

const Box = styled(Group)`
  background: transparent;

  & > :first-child {
    flex: 1;
  }
`

const FlexDiv = styled.div`
  flex: 1;
`

const DragHandle = styled(IconButton)`
  cursor: grab;
  z-index: 1;
  margin-left: ${rem(4)};

  /* To prevent unwanted scrolling when dragging. See https://docs.dndkit.com/api-documentation/draggable#recommendations */
  touch-action: none;

  .drag-overlay & {
    pointer-events: none;
  }

  @media print {
    visibility: hidden;
  }

  @media screen {
    ${mediaQueryUp.desktop} {
      &.showOnlyOnHover {
        visibility: hidden;

        ${Box}:hover &, &.isDragging {
          visibility: visible;
        }
      }
    }
  }
`

interface Props {
  id: string
  data?: UseDraggableArguments['data']
  handlesOnlyOnHover?: boolean
}

function DraggableItemWrapper({
  id,
  children,
  data,
  handlesOnlyOnHover,
}: React.PropsWithChildren<Props>) {
  const intl = useIntl()
  const [isTooltipOpen, setIsTooltipOpen] = React.useState<boolean | undefined>(undefined)
  const {
    permissions: { canEdit },
  } = useContext(TripContext)!

  const { isDragging, setNodeRef, listeners, attributes, setActivatorNodeRef } =
    useDraggable({
      id,
      data,
      disabled: !canEdit,
    })

  const draggedItemHeight = useContext(DraggedItemHeightContext)

  useEffect(() => {
    if (isDragging) {
      setIsTooltipOpen(false)
    } else {
      setIsTooltipOpen(undefined)
    }
  }, [isDragging])

  const isDesktopWithoutTouch = useDesktopWithoutTouch()
  const rootProps = isDesktopWithoutTouch ? { ...attributes, ...listeners } : {}
  const dragHandleProps = isDesktopWithoutTouch ? {} : { ...attributes, ...listeners }

  return (
    <Root
      id={`draggable-item-${id}`}
      ref={setNodeRef}
      className={clsx({
        collapsed: isDragging && draggedItemHeight > 0,
      })}
      {...rootProps}
    >
      <Box
        direction="horizontal"
        verticalAlign="center"
        className={clsx({ dragEnabled: canEdit })}
      >
        <FlexDiv className={clsx({ dragEnabled: canEdit })}>{children}</FlexDiv>
        {canEdit && (
          <Tooltip
            description={intl.formatMessage({
              id: 'CTpQEbXZOK9XagbWWNgb',
              defaultMessage: 'Drag to reorder',
              description: 'Tooltip for drag handle to reorder trip planner items',
            })}
            open={isTooltipOpen}
            portaled
          >
            <DragHandle
              kind="tertiary"
              className={clsx({
                isDragging,
                showOnlyOnHover: handlesOnlyOnHover && isDesktopWithoutTouch,
              })}
              {...dragHandleProps}
              size="small"
              ref={isDesktopWithoutTouch ? undefined : setActivatorNodeRef}
            >
              <LineDraggabledotsIcon />
            </DragHandle>
          </Tooltip>
        )}
      </Box>
    </Root>
  )
}

export default DraggableItemWrapper
